import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { 
  User, Mail, Phone, MapPin, Camera, Save, Loader2, 
  CheckCircle2, Calendar, Wallet, TrendingUp, Edit3, Globe
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    country: "",
    city: "",
    bio: "",
    profile_image: ""
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setFormData({
        full_name: res.data.full_name || "",
        phone: res.data.phone || "",
        country: res.data.country || "",
        city: res.data.city || "",
        bio: res.data.bio || "",
        profile_image: res.data.profile_image || ""
      });
    } catch (e) {
      console.error("Error cargando perfil:", e);
      // Solo mostrar error si es un problema de autenticación
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error("Sesión expirada");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update localStorage with new user data
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({
        ...storedUser,
        ...res.data.user
      }));
      
      setProfile(res.data.user);
      setEditing(false);
      toast.success("Perfil actualizado correctamente");
    } catch (e) {
      toast.error("Error al guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("La imagen no puede superar 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, profile_image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FCD535] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="border-l-4 border-[#FCD535] pl-4">
        <h1 className="text-2xl font-bold text-[#EAECEF] flex items-center gap-2">
          <User className="w-6 h-6 text-[#FCD535]" />
          Mi Perfil
        </h1>
        <p className="text-[#848E9C]">Gestiona tu información personal</p>
      </div>

      {/* Profile Card */}
      <Card className="card-dark overflow-hidden">
        <div className="bg-gradient-to-r from-[#FCD535]/10 via-[#0ECB81]/10 to-[#FCD535]/10 h-32" />
        <CardContent className="relative px-6 pb-6">
          {/* Profile Image */}
          <div className="absolute -top-16 left-6">
            <div className="relative">
              {formData.profile_image ? (
                <img 
                  src={formData.profile_image}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-[#181A20] shadow-xl"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#FCD535] to-[#F0B90B] border-4 border-[#181A20] shadow-xl flex items-center justify-center">
                  <span className="text-4xl font-bold text-[#0B0E11]">
                    {getInitials(formData.full_name)}
                  </span>
                </div>
              )}
              
              {/* Badge de verificación estilo Instagram */}
              {profile?.verified && (
                <div className="absolute bottom-2 right-2 w-8 h-8 bg-[#1DA1F2] rounded-full flex items-center justify-center border-[3px] border-[#181A20] shadow-lg" title="Cuenta verificada">
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                  </svg>
                </div>
              )}
              
              {editing && (
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-10 h-10 bg-[#FCD535] rounded-full flex items-center justify-center shadow-lg hover:bg-[#F0B90B] transition-colors"
                  data-testid="change-photo-btn"
                >
                  <Camera className="w-5 h-5 text-[#0B0E11]" />
                </button>
              )}
              <input 
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
            </div>
          </div>

          {/* Edit Button */}
          <div className="flex justify-end pt-2">
            {!editing ? (
              <Button 
                onClick={() => setEditing(true)}
                className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold"
                data-testid="edit-profile-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Editar Perfil
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      full_name: profile?.full_name || "",
                      phone: profile?.phone || "",
                      country: profile?.country || "",
                      city: profile?.city || "",
                      bio: profile?.bio || "",
                      profile_image: profile?.profile_image || ""
                    });
                  }}
                  className="border-[#2B3139] text-[#848E9C]"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#0ECB81] hover:bg-[#0ECB81]/80 text-white font-semibold"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar
                </Button>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="mt-8 pt-4">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-[#EAECEF]">
                {editing ? (
                  <Input 
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="input-dark text-2xl font-bold h-auto py-1"
                    placeholder="Tu nombre"
                    data-testid="input-name"
                  />
                ) : (
                  profile?.full_name || "Usuario"
                )}
              </h2>
              <Badge className="bg-[#0ECB81]/20 text-[#0ECB81] border-0">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Verificado
              </Badge>
            </div>
            <p className="text-[#848E9C] flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {profile?.email}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dark">
          <CardContent className="p-4 text-center">
            <Wallet className="w-6 h-6 text-[#FCD535] mx-auto mb-2" />
            <p className="text-2xl font-bold font-mono text-[#EAECEF]">
              €{(profile?.balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
            </p>
            <p className="text-xs text-[#848E9C]">Balance</p>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-[#0ECB81] mx-auto mb-2" />
            <p className="text-2xl font-bold font-mono text-[#0ECB81]">
              +€{(profile?.total_profit || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
            </p>
            <p className="text-xs text-[#848E9C]">Ganancias</p>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="p-4 text-center">
            <Calendar className="w-6 h-6 text-[#3B82F6] mx-auto mb-2" />
            <p className="text-lg font-bold text-[#EAECEF]">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "-"}
            </p>
            <p className="text-xs text-[#848E9C]">Miembro desde</p>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="w-6 h-6 text-[#0ECB81] mx-auto mb-2" />
            <p className="text-lg font-bold text-[#0ECB81]">Activa</p>
            <p className="text-xs text-[#848E9C]">Cuenta</p>
          </CardContent>
        </Card>
      </div>

      {/* Personal Information */}
      <Card className="card-dark">
        <CardHeader className="border-b border-[#2B3139]">
          <CardTitle className="text-[#EAECEF] flex items-center gap-2">
            <User className="w-5 h-5 text-[#FCD535]" />
            Información Personal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-[#EAECEF] flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#848E9C]" />
                Teléfono
              </Label>
              {editing ? (
                <Input 
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-dark"
                  placeholder="+34 612 345 678"
                  data-testid="input-phone"
                />
              ) : (
                <p className="text-[#EAECEF] py-2">
                  {profile?.phone || <span className="text-[#848E9C]">No especificado</span>}
                </p>
              )}
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label className="text-[#EAECEF] flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#848E9C]" />
                País
              </Label>
              {editing ? (
                <Input 
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className="input-dark"
                  placeholder="España"
                  data-testid="input-country"
                />
              ) : (
                <p className="text-[#EAECEF] py-2">
                  {profile?.country || <span className="text-[#848E9C]">No especificado</span>}
                </p>
              )}
            </div>

            {/* City */}
            <div className="space-y-2">
              <Label className="text-[#EAECEF] flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#848E9C]" />
                Ciudad
              </Label>
              {editing ? (
                <Input 
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="input-dark"
                  placeholder="Madrid"
                  data-testid="input-city"
                />
              ) : (
                <p className="text-[#EAECEF] py-2">
                  {profile?.city || <span className="text-[#848E9C]">No especificado</span>}
                </p>
              )}
            </div>

            {/* Email - Read only */}
            <div className="space-y-2">
              <Label className="text-[#EAECEF] flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#848E9C]" />
                Email
              </Label>
              <p className="text-[#EAECEF] py-2 flex items-center gap-2">
                {profile?.email}
                <Badge className="text-xs bg-[#2B3139] text-[#848E9C]">No editable</Badge>
              </p>
            </div>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label className="text-[#EAECEF]">Biografía</Label>
            {editing ? (
              <Textarea 
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                className="input-dark min-h-[100px]"
                placeholder="Cuéntanos algo sobre ti..."
                data-testid="input-bio"
              />
            ) : (
              <p className="text-[#EAECEF] py-2 bg-[#0B0E11] rounded-lg p-4 border border-[#2B3139]">
                {profile?.bio || <span className="text-[#848E9C] italic">No has añadido una biografía todavía</span>}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card className="card-dark border-[#2B3139]">
        <CardHeader className="border-b border-[#2B3139]">
          <CardTitle className="text-[#EAECEF] flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#0ECB81]" />
            Seguridad de la Cuenta
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#2B3139]">
              <div>
                <p className="text-[#EAECEF] font-medium">Verificación de Email</p>
                <p className="text-sm text-[#848E9C]">Tu email ha sido verificado</p>
              </div>
              <Badge className="bg-[#0ECB81]/20 text-[#0ECB81]">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Verificado
              </Badge>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-[#2B3139]">
              <div>
                <p className="text-[#EAECEF] font-medium">Autenticación de dos factores</p>
                <p className="text-sm text-[#848E9C]">Añade una capa extra de seguridad</p>
              </div>
              <Badge className="bg-[#F0B90B]/20 text-[#F0B90B]">
                Pendiente
              </Badge>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-[#EAECEF] font-medium">Verificación de Identidad (KYC)</p>
                <p className="text-sm text-[#848E9C]">Verifica tu identidad para límites más altos</p>
              </div>
              <Badge className="bg-[#0ECB81]/20 text-[#0ECB81]">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Aprobado
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
